import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomProductList, CustomSearchCriteria } from '@catalog/const';
import { CategoryTagsFacade } from '@catalog/data-access';
import {
  ActivatedRouterStateSnapshot,
  ProductSearchPage,
  RouterState,
  RoutingService,
  deepMerge,
} from '@spartacus/core';
import { ViewConfig } from '@spartacus/storefront';
import { EMPTY, Observable, Subject, combineLatest, of, using } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ProductListService } from './product-list.service';

@Injectable({ providedIn: 'root' })
export class ProductListComponentService {
  readonly model$: Observable<ProductSearchPage> = using(
    () => this.search$.subscribe(),
    () => this.searchResults$,
  ).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
    filter((m) => Object.keys(m).length > 0),
  );

  private triggerReloadResults$ = new Subject<number>();

  /**
   * Актуальные query параметры из строки поиска
   */
  private searchCriteriaByRouting$: Observable<CustomSearchCriteria | undefined> = this.routing.getRouterState().pipe(
    distinctUntilChanged((x: RouterState, y: RouterState) => {
      return x.state.url === y.state.url;
    }),
    map((routerState: RouterState) => routerState.state),
    filter((state: ActivatedRouterStateSnapshot) => {
      return (
        !!state.semanticRoute &&
        ['category', 'search'].concat(Object.values(CustomProductList)).includes(state.semanticRoute)
      );
    }),
    switchMap((state: ActivatedRouterStateSnapshot) => {
      if (state.semanticRoute) {
        return this.productListService.getProductListFacets(state.semanticRoute, state.params).pipe(
          map((facets: string) => {
            // обогащаем query поиска по товарам для кастомных листингов
            return deepMerge({}, { queryParams: { facets } }, state);
          }),
        );
      }

      return EMPTY;
    }),
    map((state: ActivatedRouterStateSnapshot | undefined) => {
      if (state) {
        return {
          facets: state.queryParams.facets,
          freeTextSearch: state.queryParams.freeTextSearch,
          sort: state.queryParams.sort,
          pageSize: state.queryParams.pageSize || this.config.view?.defaultPageSize,
          currentPage: state.queryParams.currentPage || 0,
        };
      }
    }),
  );

  private search$ = combineLatest({
    triggerReloadResults: this.triggerReloadResults$,
    searchCriteriaByRouting: this.searchCriteriaByRouting$,
  }).pipe(
    filter(Boolean),
    distinctUntilChanged((prev, next) => {
      return (
        prev &&
        next &&
        prev.searchCriteriaByRouting?.freeTextSearch === next.searchCriteriaByRouting?.freeTextSearch &&
        prev.searchCriteriaByRouting?.sort === next.searchCriteriaByRouting?.sort &&
        prev.searchCriteriaByRouting?.facets === next.searchCriteriaByRouting?.facets &&
        prev.triggerReloadResults === next.triggerReloadResults
      );
    }),
    switchMap(({ searchCriteriaByRouting: criteria }) => this.productListService.distinctSearch(criteria)),
  );

  private searchResults$: Observable<ProductSearchPage> = this.productListService
    .getResults()
    .pipe(filter((m) => Object.keys(m).length > 0));

  constructor(
    private config: ViewConfig,
    private routing: RoutingService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private categoryTagsFacade: CategoryTagsFacade,
    private productListService: ProductListService,
  ) {}

  getPageItems(pageNumber: number): void {
    this.searchCriteriaByRouting$.pipe(take(1)).subscribe((routeCriteria) => {
      const criteria = {
        ...routeCriteria,
        currentPage: pageNumber,
      };

      this.search(criteria);
    });
  }

  loadMore(currentPage: number): void {
    this.route({ currentPage });

    this.searchCriteriaByRouting$.pipe(take(1)).subscribe((routeCriteria) => {
      const criteria = {
        ...routeCriteria,
        currentPage,
      };

      this.productListService.loadMore(criteria);
    });
  }

  submitFacets(): Observable<never> {
    return this.productListService.getNextQuery().pipe(
      tap((facets) => this.setQueryParams({ facets, currentPage: 0 })),
      switchMap(() => EMPTY),
    );
  }

  sort(sort: string): void {
    this.productListService.resetPageNumbers();
    this.route({ sort, currentPage: 0 });
    this.triggerReloadResults();
  }

  triggerReloadResults(): void {
    this.triggerReloadResults$.next(Math.random());
  }

  private getUrl(): Observable<string[]> {
    return this.routing.getRouterState().pipe(
      map((routerState) => routerState.state.semanticRoute),
      switchMap((semanticRoute) =>
        semanticRoute === CustomProductList.SeoProductList
          ? this.categoryTagsFacade.getActiveCategoryTagItem().pipe(
              filter(Boolean),
              map((item) => ['category/', item.categoryCode]),
            )
          : of([]),
      ),
    );
  }

  private route(queryParams: CustomSearchCriteria): void {
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute,
    });
  }

  private search(criteria: CustomSearchCriteria): void {
    this.productListService.search(criteria);
  }

  private setQueryParams(queryParams: CustomSearchCriteria): void {
    this.getUrl()
      .pipe(take(1))
      .subscribe((url) => {
        this.router.navigate(url, {
          queryParams,
          queryParamsHandling: 'merge',
        });
      });
  }
}
